/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/useScopeAgRowActions.ts
 * FILE_ID: QB-AGGRID-ROW-ACTIONS-HOOK-001
 * PURPOSE: Memoised per-row Delete / Duplicate callbacks used by the
 *          SAM-style row-actions column (Copy + Delete icons) and by
 *          the formula bar's "delete focused" key. Single source of
 *          truth for "what happens when an estimator removes or
 *          clones a real row" so the icon path and the keyboard path
 *          stay in sync.
 * WHY: Extracted out of ScopeAgGridSection.tsx so that file stays
 *      under 300 lines after MR 7 added the duplicate path.
 * FLOW:
 *   ┌──────────────┐    ┌──────────────────┐    ┌──────────────────────┐
 *   │ user click / │ -> │ guard (editable, │ -> │ onDeleteItem /       │
 *   │ key          │    │  not a pad row)  │    │ onAddItem (clone)    │
 *   └──────────────┘    └──────────────────┘    └──────────────────────┘
 * DEPENDENCIES: react, pad-row-helpers
 * RELATED:
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection.tsx) — consumer
 *   - QB-AGGRID-ROWACTIONS-001 (ScopeAgGridRowActions.tsx) — caller
 */

import { useCallback } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import { isPadRowId } from './pad-row-helpers'

export interface UseScopeAgRowActionsArgs {
  sectionId: string
  rowCount: number
  isEditable: boolean
  onAddItem: (sectionId: string, data: Record<string, unknown>) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
}

export interface UseScopeAgRowActionsResult {
  handleDeleteRow: (rowId: string) => void
  handleDuplicateRow: (row: QuoteLineItem) => void
}

export function useScopeAgRowActions(
  args: UseScopeAgRowActionsArgs,
): UseScopeAgRowActionsResult {
  const { sectionId, rowCount, isEditable, onAddItem, onDeleteItem } = args

  // Routes the per-row trash icon (and the formula-bar Delete key)
  // through onDeleteItem so the buffer / refetch dance is consistent.
  const handleDeleteRow = useCallback(
    (rowId: string) => {
      if (!isEditable) return
      if (isPadRowId(rowId)) return
      onDeleteItem(sectionId, rowId)
    },
    [isEditable, onDeleteItem, sectionId],
  )

  // Clones every persisted scalar from the source row into a fresh
  // create-payload, drops the id, bumps sort_order so the new row
  // lands right after the source. The backend serializer is permissive
  // about extra keys so the raw shape is safe to forward.
  const handleDuplicateRow = useCallback(
    (row: QuoteLineItem) => {
      if (!isEditable) return
      if (isPadRowId(row.id)) return
      const src = row as unknown as Record<string, unknown>
      const payload: Record<string, unknown> = {}
      for (const key of Object.keys(src)) {
        if (key === 'id' || key === 'sort_order') continue
        payload[key] = src[key]
      }
      payload.sort_order = (row.sort_order ?? rowCount) + 1
      onAddItem(sectionId, payload)
    },
    [isEditable, onAddItem, rowCount, sectionId],
  )

  return { handleDeleteRow, handleDuplicateRow }
}
